import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterOption, FilterSizeVariants } from "../types";
import { useSizeFilterHelpers } from "./useSizeFilterHelpers";

const VISIBLE_OPTIONS_LIMIT = 7;

export const useSizeFilterAutoInit = ({
  facetId,
  mode,
  modeOrigin,
  setMode,
  simpleGroups,
  advancedGroups,
  selectedFilters,
  filter,
  filterOptionsToCurrentFacet
}: {
  facetId: string;
  mode: FilterSizeVariants;
  modeOrigin: "user" | "auto";
  setMode: (nextMode: FilterSizeVariants, reason: "user" | "auto") => void;
  simpleGroups: any[];
  advancedGroups: any[];
  selectedFilters: Record<string, any>;
  filter: any;
  filterOptionsToCurrentFacet: (
    opts: FilterOption[] | undefined | null
  ) => FilterOption[];
}) => {
  const {
    getNodeKey,
    flattenLeafOptions,
    getOptionSizeFormat,
    collectAvailableFormatsFromGroup,
    findGroupContainingSelectedOption,
    findFirstSelectedOptionPath
  } = useSizeFilterHelpers();

  const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>([]);
  const [expandedAccordionKey, setExpandedAccordionKey] = useState<
    string | null
  >(null);
  const [userToggledAccordionManually, setUserToggledAccordionManually] =
    useState(false);
  const [isShowAllExpandedByGroup, setIsShowAllExpandedByGroup] = useState<
    Record<string, boolean>
  >({});
  const [activeSizeFormatByGroup, setActiveSizeFormatByGroup] = useState<
    Record<string, string>
  >({});
  const [activeTabByGroup, setActiveTabByGroup] = useState<
    Record<string, string>
  >({});
  const [activeSubgroupByGroup, setActiveSubgroupByGroup] = useState<
    Record<string, string>
  >({});
  const [advancedLeafOptionsByGroup, setAdvancedLeafOptionsByGroup] = useState<
    Record<string, FilterOption[]>
  >({});

  // After first scroll; gates format inference during init (show-all runs before).
  const initialSetupDoneRef = useRef(false);
  const simpleAutoInitFacetIdRef = useRef<string | null>(null);
  const generalAutoInitFacetIdRef = useRef<string | null>(null);
  const groupsAlreadyScrolledRef = useRef<Set<string>>(new Set());
  const userInteractedWithSizesRef = useRef(false);
  const advancedTabAlignedForFacetRef = useRef<string | null>(null);
  const previousSelectedKeysInGroupRef = useRef<Record<string, string[]>>({});

  const selectedSizeKeys = useMemo(
    () => selectedOptions.map(option => option.key).filter(Boolean) as string[],
    [selectedOptions]
  );

  const previousFacetIdRef = useRef(facetId);

  useEffect(() => {
    if (previousFacetIdRef.current === facetId) return;
    previousFacetIdRef.current = facetId;

    const groupsForCurrentMode =
      mode === FilterSizeVariants.SIMPLE ? simpleGroups : advancedGroups;

    setIsShowAllExpandedByGroup({});
    setActiveSizeFormatByGroup({});
    setActiveTabByGroup({});
    setActiveSubgroupByGroup({});
    setUserToggledAccordionManually(false);

    initialSetupDoneRef.current = false;
    simpleAutoInitFacetIdRef.current = null;
    generalAutoInitFacetIdRef.current = null;
    groupsAlreadyScrolledRef.current.clear();
    advancedTabAlignedForFacetRef.current = null;
    previousSelectedKeysInGroupRef.current = {};

    const allLeafOptions = groupsForCurrentMode.flatMap(group =>
      flattenLeafOptions(group)
    );

    const contextOpts = selectedFilters?.[filter.queryParam]?.selectedOptions;
    const contextHasSizes =
      Array.isArray(contextOpts) && contextOpts.length > 0;
    const contextExplicitlyEmpty =
      selectedFilters && !selectedFilters[filter.queryParam];

    let preselectedOptions: FilterOption[];
    if (contextExplicitlyEmpty) {
      preselectedOptions = [];
    } else if (contextHasSizes) {
      preselectedOptions = filterOptionsToCurrentFacet(contextOpts);
    } else {
      preselectedOptions = allLeafOptions.filter(option => option.isSelected);
    }
    setSelectedOptions(preselectedOptions);

    const preselectedKeys = preselectedOptions
      .map(option => (typeof option?.key === "string" ? option.key : null))
      .filter(Boolean) as string[];

    const groupWithPreselection =
      preselectedKeys.length > 0
        ? groupsForCurrentMode.find(group => {
            const groupKey = getNodeKey(group) || "";
            const groupKeyPrefix = `${groupKey}+`;
            return preselectedKeys.some(
              key => key === groupKey || key.startsWith(groupKeyPrefix)
            );
          })
        : null;

    setExpandedAccordionKey(
      groupWithPreselection ? getNodeKey(groupWithPreselection) || "" : null
    );
  }, [
    facetId,
    simpleGroups,
    advancedGroups,
    mode,
    flattenLeafOptions,
    getNodeKey
  ]);

  useEffect(() => {
    const optionsFromContext =
      selectedFilters?.[filter.queryParam]?.selectedOptions;

    if (Array.isArray(optionsFromContext)) {
      setSelectedOptions(filterOptionsToCurrentFacet(optionsFromContext));
    } else if (selectedFilters && !selectedFilters[filter.queryParam]) {
      setSelectedOptions([]);
    }
  }, [selectedFilters, filter.queryParam, filterOptionsToCurrentFacet]);

  useEffect(() => {
    userInteractedWithSizesRef.current = false;
  }, [facetId]);

  useEffect(() => {
    if (simpleAutoInitFacetIdRef.current === facetId) return;
    if (selectedSizeKeys.length === 0) return;
    if (mode !== FilterSizeVariants.SIMPLE) return;

    const { groupKey, format } = findGroupContainingSelectedOption(
      simpleGroups,
      selectedSizeKeys
    );

    if (groupKey) setExpandedAccordionKey(groupKey);
    if (groupKey && format) {
      setActiveSizeFormatByGroup(prev => ({ ...prev, [groupKey]: format }));
    }

    simpleAutoInitFacetIdRef.current = facetId;
  }, [
    facetId,
    selectedSizeKeys,
    simpleGroups,
    mode,
    findGroupContainingSelectedOption
  ]);

  const allAdvancedOptionKeys = useMemo(() => {
    const keySet = new Set<string>();
    advancedGroups.forEach(group => {
      flattenLeafOptions(group).forEach(option => {
        if (typeof option?.key === "string") keySet.add(option.key);
      });
    });
    return keySet;
  }, [advancedGroups, flattenLeafOptions]);

  useEffect(() => {
    if (generalAutoInitFacetIdRef.current === facetId) return;
    if (modeOrigin === "user") return;
    if (selectedSizeKeys.length === 0) return;

    const shouldAutoSwitchToAdvanced =
      mode === FilterSizeVariants.SIMPLE &&
      selectedSizeKeys.some(key => allAdvancedOptionKeys.has(key));

    if (shouldAutoSwitchToAdvanced) {
      setMode(FilterSizeVariants.ADVANCED, "auto");
      return;
    }

    const targetGroups =
      mode === FilterSizeVariants.SIMPLE ? simpleGroups : advancedGroups;
    const selectionPath = findFirstSelectedOptionPath(
      targetGroups,
      new Set(selectedSizeKeys)
    );

    if (!selectionPath) {
      generalAutoInitFacetIdRef.current = facetId;
      return;
    }

    const { groupKey, tabKey, subgroupKey, format } = selectionPath;

    if (!userToggledAccordionManually && groupKey) {
      setExpandedAccordionKey(groupKey);
    }

    if (mode === FilterSizeVariants.SIMPLE && groupKey && format) {
      setActiveSizeFormatByGroup(prev => ({ ...prev, [groupKey]: format }));
    }

    if (mode === FilterSizeVariants.ADVANCED && groupKey) {
      if (tabKey) {
        setActiveTabByGroup(prev => ({ ...prev, [groupKey]: tabKey }));
      }
      if (subgroupKey) {
        setActiveSubgroupByGroup(prev => ({
          ...prev,
          [groupKey]: subgroupKey
        }));
      }
    }

    generalAutoInitFacetIdRef.current = facetId;
  }, [
    facetId,
    modeOrigin,
    selectedSizeKeys,
    userToggledAccordionManually,
    mode,
    simpleGroups,
    advancedGroups,
    allAdvancedOptionKeys,
    setMode,
    findFirstSelectedOptionPath
  ]);

  const expandedGroupNode = useMemo(() => {
    if (!expandedAccordionKey) return null;
    const groupsForCurrentMode =
      mode === FilterSizeVariants.SIMPLE ? simpleGroups : advancedGroups;
    return (
      groupsForCurrentMode.find(
        group => (getNodeKey(group) || "") === expandedAccordionKey
      ) || null
    );
  }, [simpleGroups, advancedGroups, mode, expandedAccordionKey, getNodeKey]);

  useEffect(() => {
    if (advancedTabAlignedForFacetRef.current === facetId) return;
    if (!expandedAccordionKey || mode !== FilterSizeVariants.ADVANCED) return;
    if (!expandedGroupNode?.children?.length) return;

    const selectedKeySet = new Set(selectedSizeKeys);
    if (selectedKeySet.size === 0) return;

    const tabs = expandedGroupNode.children;
    for (const tab of tabs) {
      if (!Array.isArray(tab?.children)) continue;
      for (const subgroup of tab.children) {
        const leafOptions = flattenLeafOptions(subgroup);
        const containsSelectedSize = leafOptions.some(
          (option: any) => option?.key && selectedKeySet.has(option.key)
        );
        if (!containsSelectedSize) continue;

        const tabKey = getNodeKey(tab);
        const subgroupKey = getNodeKey(subgroup);

        if (tabKey && activeTabByGroup[expandedAccordionKey] !== tabKey) {
          setActiveTabByGroup(prev => ({
            ...prev,
            [expandedAccordionKey]: tabKey
          }));
        }
        if (
          subgroupKey &&
          activeSubgroupByGroup[expandedAccordionKey] !== subgroupKey
        ) {
          setActiveSubgroupByGroup(prev => ({
            ...prev,
            [expandedAccordionKey]: subgroupKey
          }));
        }
        advancedTabAlignedForFacetRef.current = facetId;
        return;
      }
    }
  }, [
    facetId,
    expandedAccordionKey,
    expandedGroupNode,
    mode,
    selectedSizeKeys,
    flattenLeafOptions,
    getNodeKey,
    activeTabByGroup,
    activeSubgroupByGroup
  ]);

  const allLeafOptionsInExpandedGroup = useMemo(() => {
    if (!expandedGroupNode) return [];
    return flattenLeafOptions(expandedGroupNode);
  }, [expandedGroupNode, flattenLeafOptions]);

  const availableFormatsInExpandedGroup = useMemo(() => {
    if (mode !== FilterSizeVariants.SIMPLE || !expandedGroupNode) return [];
    return collectAvailableFormatsFromGroup(expandedGroupNode);
  }, [mode, expandedGroupNode, collectAvailableFormatsFromGroup]);

  useEffect(() => {
    if (!expandedAccordionKey || mode !== FilterSizeVariants.SIMPLE) return;
    if (availableFormatsInExpandedGroup.length < 2) return;

    const currentFormat = activeSizeFormatByGroup[expandedAccordionKey];

    const selectedKeysInGroup = selectedSizeKeys.filter(key =>
      allLeafOptionsInExpandedGroup.some(option => option.key === key)
    );

    const previousSelectedKeysInGroup =
      previousSelectedKeysInGroupRef.current[expandedAccordionKey] || [];

    const addedKeys = selectedKeysInGroup.filter(
      key => !previousSelectedKeysInGroup.includes(key)
    );

    const hasAddedKeys = addedKeys.length > 0;

    if (hasAddedKeys) {
      const lastAddedKey =
        [...selectedSizeKeys].reverse().find(key => addedKeys.includes(key)) ||
        addedKeys[addedKeys.length - 1];

      const matchedOption = lastAddedKey
        ? allLeafOptionsInExpandedGroup.find(
            option => option.key === lastAddedKey
          )
        : null;

      const inferredFormat = matchedOption
        ? getOptionSizeFormat(matchedOption)
        : null;

      if (
        inferredFormat &&
        availableFormatsInExpandedGroup.includes(inferredFormat) &&
        currentFormat !== inferredFormat
      ) {
        setActiveSizeFormatByGroup(prev => ({
          ...prev,
          [expandedAccordionKey]: inferredFormat
        }));
      }
    }

    previousSelectedKeysInGroupRef.current[expandedAccordionKey] =
      selectedKeysInGroup;

    if (
      currentFormat &&
      availableFormatsInExpandedGroup.includes(currentFormat)
    ) {
      return;
    }
    setActiveSizeFormatByGroup(prev => ({
      ...prev,
      [expandedAccordionKey]: availableFormatsInExpandedGroup.includes("EU")
        ? "EU"
        : availableFormatsInExpandedGroup[0]
    }));
  }, [
    expandedAccordionKey,
    availableFormatsInExpandedGroup,
    mode,
    activeSizeFormatByGroup,
    selectedSizeKeys,
    allLeafOptionsInExpandedGroup,
    getOptionSizeFormat
  ]);

  const formatFilteredOptionsInExpandedGroup = useMemo(() => {
    if (!expandedGroupNode || mode !== FilterSizeVariants.SIMPLE) return [];

    let options = allLeafOptionsInExpandedGroup;

    if (availableFormatsInExpandedGroup.length >= 2 && expandedAccordionKey) {
      const activeFormat =
        activeSizeFormatByGroup[expandedAccordionKey] ||
        (availableFormatsInExpandedGroup.includes("EU")
          ? "EU"
          : availableFormatsInExpandedGroup[0]);

      options = allLeafOptionsInExpandedGroup.filter(
        option => getOptionSizeFormat(option) === activeFormat
      );
    }

    return options;
  }, [
    expandedGroupNode,
    expandedAccordionKey,
    getOptionSizeFormat,
    activeSizeFormatByGroup,
    allLeafOptionsInExpandedGroup,
    availableFormatsInExpandedGroup,
    mode
  ]);

  useEffect(() => {
    if (userInteractedWithSizesRef.current) return;
    if (mode !== FilterSizeVariants.SIMPLE) return;
    if (!expandedAccordionKey) return;

    const firstSelectedKeyInGroup = selectedSizeKeys.find(key =>
      formatFilteredOptionsInExpandedGroup.some(option => option.key === key)
    );
    if (!firstSelectedKeyInGroup) return;

    const selectedIndex = formatFilteredOptionsInExpandedGroup.findIndex(
      option => option.key === firstSelectedKeyInGroup
    );
    if (selectedIndex < VISIBLE_OPTIONS_LIMIT) return;

    setIsShowAllExpandedByGroup(prev => {
      if (prev[expandedAccordionKey]) return prev;
      return { ...prev, [expandedAccordionKey]: true };
    });
  }, [
    mode,
    expandedAccordionKey,
    formatFilteredOptionsInExpandedGroup,
    selectedSizeKeys
  ]);

  const currentFormatForExpandedGroup = expandedAccordionKey
    ? activeSizeFormatByGroup[expandedAccordionKey]
    : undefined;

  const isShowAllActiveForExpandedGroup = expandedAccordionKey
    ? !!isShowAllExpandedByGroup[expandedAccordionKey]
    : false;

  useEffect(() => {
    if (!expandedAccordionKey) return;
    groupsAlreadyScrolledRef.current.delete(expandedAccordionKey);
  }, [
    expandedAccordionKey,
    currentFormatForExpandedGroup,
    formatFilteredOptionsInExpandedGroup,
    isShowAllActiveForExpandedGroup
  ]);

  useEffect(() => {
    if (userInteractedWithSizesRef.current) return;
    if (!expandedAccordionKey) return;
    if (groupsAlreadyScrolledRef.current.has(expandedAccordionKey)) return;

    const visibleOptions =
      mode === FilterSizeVariants.SIMPLE
        ? formatFilteredOptionsInExpandedGroup
        : advancedLeafOptionsByGroup[expandedAccordionKey] || [];

    const firstSelectedKey = selectedSizeKeys.find(key =>
      visibleOptions.some(option => option.key === key)
    );

    if (!firstSelectedKey) return;

    const selectedIndex = visibleOptions.findIndex(
      option => option.key === firstSelectedKey
    );
    const isSelectedBelowFold = selectedIndex >= VISIBLE_OPTIONS_LIMIT;

    const scrollDelay = isSelectedBelowFold ? 200 : 80;
    setTimeout(() => {
      const element = document.querySelector(
        `[data-size-key="${firstSelectedKey}"]`
      ) as HTMLElement | null;

      if (element && typeof element.scrollIntoView === "function") {
        element.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      initialSetupDoneRef.current = true;
    }, scrollDelay);

    groupsAlreadyScrolledRef.current.add(expandedAccordionKey);
  }, [
    mode,
    expandedAccordionKey,
    selectedSizeKeys,
    isShowAllExpandedByGroup,
    formatFilteredOptionsInExpandedGroup,
    advancedLeafOptionsByGroup
  ]);

  const toggleAccordionGroup = useCallback((groupKey: string) => {
    setUserToggledAccordionManually(true);
    setExpandedAccordionKey(current =>
      current === groupKey ? null : groupKey
    );
  }, []);

  const updateAdvancedLeafOptions = useCallback(
    (groupKey: string, options: FilterOption[]) => {
      setAdvancedLeafOptionsByGroup(prev => ({
        ...prev,
        [groupKey]: options
      }));
    },
    []
  );

  return {
    selectedOptions,
    setSelectedOptions,
    selectedSizeKeys,
    expandedAccordionKey,
    setExpandedAccordionKey,
    expandedGroupNode,
    allLeafOptionsInExpandedGroup,
    availableFormatsInExpandedGroup,
    formatFilteredOptionsInExpandedGroup,
    isShowAllExpandedByGroup,
    setIsShowAllExpandedByGroup,
    activeSizeFormatByGroup,
    setActiveSizeFormatByGroup,
    activeTabByGroup,
    setActiveTabByGroup,
    activeSubgroupByGroup,
    setActiveSubgroupByGroup,
    advancedLeafOptionsByGroup,
    userInteractedWithSizesRef,
    toggleAccordionGroup,
    updateAdvancedLeafOptions,
    setUserToggledAccordionManually
  };
};
